import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Net } from 'src/app/data-models/user-models/net.model';
import { UserData } from 'src/app/data-models/user-models/user-data.model';
import { SharedMethodsService } from 'src/app/services/shared-methods.service';
import { CommonDataService } from 'src/app/services/storage/common-data.service';

@Component({
    selector: 'app-add-net-dialog',
    templateUrl: './add-net-dialog.component.html',
    styleUrls: ['./add-net-dialog.component.css'],
    standalone: false
})
export class AddNetDialogComponent implements OnInit {

  //data preambles
  netTypes: string[] = []; // holds net type values
  meshDigits: string[] = ['4', '5', '6', '7', '8', '9', '10', '11', '12']
  meshFractions: string[] = ['0', '1/8', '2/8', '3/8', '4/8', '5/8', '6/8', '7/8']; //hardcoded values to be used in the form
  unitTypes: string[] = ['cm', 'in', 'mm', 'meters']; //hardcoded values to be used in the form
  duplicateFlag: boolean = false; // used if the name & type of the new net are not unique
  editNet?: Net; // the net being edited; undefined when adding a new net
  dialogMode: string = 'add'; // 'add' or 'edit' - drives the ICU select translations in the template

  dialogReturn: Net = new Net();

  netForm: UntypedFormGroup = new UntypedFormGroup({
    netType: new FormControl<string>('', [Validators.required]),
    numStrands: new FormControl<number>(null!, Validators.max(99)), // conditional validation
    netName: new FormControl<string>('', [Validators.required]), 
    netLength: new FormControl<string>('', [Validators.required]), 
    netDepth: new FormControl<number>(null!, [Validators.required]), 
    netUnits: new FormControl<string>('', [Validators.required]), 
    hangRatio: new FormControl<string>('', [Validators.required]), 
    meshSize: new FormControl<string>('', [Validators.required]),
    meshSizeFraction: new FormControl<string>('', [Validators.required]),
    numMeshes: new FormControl<string>('', [Validators.required, Validators.max(99999)]) 
  })
  
  constructor(
    private dialogRef: MatDialogRef<AddNetDialogComponent>,
    public sharedMethodsService: SharedMethodsService,
    public commonDataService: CommonDataService,
    @Inject(MAT_DIALOG_DATA) private data: { userDetails: UserData, net?: Net }
    ) {

      let requiredData: string[] = ['net_types'];

      commonDataService.getSpecifiedDocs(this.data.userDetails.userEmail, requiredData).then(() => {
        this.netTypes = CommonDataService.netTypes; // retrieve net types
      });

      this.editNet = this.data.net;
      this.dialogMode = this.editNet ? 'edit' : 'add';

  }


  /*  ---------- Initialization Methods  ---------- */


  ngOnInit(): void {

    this.onNetTypeChange();

    if (this.editNet) {
      this.netForm.patchValue({
        netType: this.editNet.netType,
        numStrands: this.editNet.numStrands,
        netName: this.editNet.netName,
        netLength: this.editNet.length,
        netDepth: this.editNet.weedlineDepth,
        netUnits: this.editNet.weedlineDepthUnits,
        hangRatio: this.editNet.hangRatio,
        meshSize: this.editNet.meshSize,
        meshSizeFraction: this.editNet.meshSizeFraction,
        numMeshes: this.editNet.numMeshes
      });
    }

  }

  onNetTypeChange(): void {

    this.netForm.get('netType')?.valueChanges.subscribe((value: string) => {
      const strandControl: AbstractControl<any, any> | null = this.netForm.get('numStrands');

      if (value === 'Alaska Twist') {
        strandControl?.setValidators([Validators.required]);
      } else {
        strandControl?.clearValidators();
      }
      strandControl?.updateValueAndValidity();
    });

    this.netForm.get('netUnits')?.valueChanges.subscribe((value: string) => {
      let control = this.netForm.get('netDepth');
  
      switch (value) {
        case 'cm':
          control?.setValidators([Validators.required, Validators.max(20000)]);
          break;
        case 'in':
          control?.setValidators([Validators.required, Validators.max(7874)]);
          break;
        case 'mm':
          control?.setValidators([Validators.required, Validators.max(200000)]);
          break;
        case 'meters':
          control?.setValidators([Validators.required, Validators.max(200)]);
          break;
      }
      control?.updateValueAndValidity();
    })

  }


  /*  ---------- Helper Methods  ---------- */


  netTypeVal(): string { // returns the net type the user has selected

    return this.netForm.get('netType')?.value;

  }

  // checks if a currNet's name & type is a duplicate of one of the user's pre-existing nets
  isNetUnique(): boolean {

    let userNets: Net[] = this.data.userDetails.nets;
    let currNetName: string = this.netForm.value.netName.trim();
    let currNetType: string = this.netForm.value.netType.trim();

    for (let net of userNets) {
      if (net === this.editNet) {
        continue; // don't compare the net being edited against itself
      }
      if ((currNetName === net.netName) && (currNetType === net.netType)) {
         return false; // return false if a duplicate is found
      }
    }
    return true; // return true if currNet is unique
  
  }

  onDepthUnitsChange() {

  }


  /*  ---------- Dialog Close Methods  ---------- */


  addNet(): void {

    if (this.netForm.invalid) { // ensure all required form fields are filled
      this.netForm.markAllAsTouched();
      return;
    }

    this.clearInputs(); // clear any old form inputs before saving data

    if (!this.isNetUnique()) { // check that we are not trying to add a duplicate net
      this.duplicateFlag = true;
      this.sharedMethodsService.toastNetUniqueError();
      return;
    }

    this.dialogRef.close(this.netForm.value); // return the filled-in values of the form field

  }


  deleteNet(): void { 

    this.dialogRef.close('delete');

  }

  // reset any dead form inputs (for conditionally displayed form fields)
  clearInputs(): void {

    if (this.netTypeVal() !== 'Alaska Twist') { // if other gear type, clear any possible input in numStrands
      this.netForm.controls['numStrands'].reset();
    }
    
  }

  //  Limits the input field to allow only digits and at most one decimal place for hang ratio
  limitDecimalPlaces(event: any) {
    const input = event.target as HTMLInputElement;
    const regex = /^\d*\.?\d{0,1}$/;
    if (!regex.test(input.value)) {
      input.value = input.value.slice(0, -1);
    }
  }

}
